import {ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, inject, OnInit, signal, viewChild} from '@angular/core';
import {ActivatedRoute, NavigationExtras, ParamMap, Router} from '@angular/router';
import {forkJoin, Subscription} from 'rxjs';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';


import {APP_TASK_STEP, APP_TASKS, PARAM_TASK_ID, PARAM_TASK_STEP_ID} from '../config/app-urls';
import {TaskService} from './services/task.service';
import {TaskConverterService} from './services/task-converter.service';
import {TaskComponentMappingService} from './components/task-component-mapping.service';
import {TaskStepStateViewModel} from './view-models/task-step-state.view-model';
import {TaskStepUpdateDto} from '@dto/tasks/task-step-update.dto';
import {TaskStepDetailViewModel} from './view-models/task-step-detail.view-model';
import {Validator} from '@shared/utilities/validator';
import {TaskDetailViewModel} from './view-models/task-detail.view-model';
import {TaskStepAnchorDirective} from './task-step-anchor.directive';
import {LoadingAnimationComponent} from '@shared/components/loading-animation/loading-animation.component';

@Component({
    selector: 'app-task',
    templateUrl: './task.component.html',
    styleUrls: ['./task.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [LoadingAnimationComponent, TaskStepAnchorDirective]
})
export class TaskComponent implements OnInit {

  public static readonly EXISTING_TASK_KEY = 'taskDetailViewModel';

  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly taskService = inject(TaskService);
  private readonly taskConverterService = inject(TaskConverterService);
  private readonly taskComponentMappingService = inject(TaskComponentMappingService);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);

  private subscriptions: Subscription = new Subscription();

  readonly dataLoading = signal(true);
  readonly stepLoadError = signal(false);
  readonly stepProcessing = signal(false);
  readonly task = signal<TaskDetailViewModel | undefined>(undefined);
  readonly currentStep = signal<TaskStepDetailViewModel | undefined>(undefined);
  readonly currentStepIndex = signal<number | undefined>(undefined);
  readonly maxEditableStepIndex = signal(0);

  private readonly taskDirective = viewChild(TaskStepAnchorDirective);

  ngOnInit(): void {
    this.dataLoading.set(true);
    this.stepProcessing.set(false);

    // Do not manage this ActivatedRoute subscription, per Angular documentation
    this.route.paramMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((paramMap: ParamMap) => {
      const taskId = paramMap.get(PARAM_TASK_ID.name);
      const stepId = paramMap.get(PARAM_TASK_STEP_ID.name);

      this.loadTaskStep(taskId, stepId);
    });
  }

  private loadTaskStep(taskId: string, stepId: unknown) {
    if (Validator.isNullOrWhitespace(taskId)) {
      this.handleNotFound();
      return;
    }

    // No need to load the task if this is new navigation on an existing component
    if (this.task()?.id === taskId) {
      this.processTaskStep(taskId, stepId, this.task());
      return;
    }

    // No need to reload the task if we've been passed it via a /tasks/XYZ -> /tasks/XYZ/steps/123 redirection
    const existingTaskViewModel = history.state[TaskComponent.EXISTING_TASK_KEY];
    if (existingTaskViewModel?.id === taskId) {
      this.processTaskStep(taskId, stepId, existingTaskViewModel);
      return;
    }

    this.dataLoading.set(true);

    const subscription = forkJoin([
      this.taskService.getTaskSummary(taskId),
      this.taskService.getTask(taskId),
    ]).subscribe({
      next: ([taskSummary, taskDetail]) => {
        const taskViewModel = this.taskConverterService.convertTaskDetailDtoToTaskDetailViewModel(taskSummary, taskDetail);
        this.processTaskStep(taskId, stepId, taskViewModel);
      },
      error: _err => {
        this.handleNotFound();
      }
    });

    this.subscriptions.add(subscription);
  }

  private handleNotFound() {
    this.router.navigate(APP_TASKS.toRouterLinkCommands());
    // TECHDEBT: Switch to a real 404 page when Azure #7378 / Pivotal #178894411 is implemented
    // this.router.navigate(APP_404.toRouterLinkCommands(), {skipLocationChange: true});
  }

  private processTaskStep(taskId: string, stepId: unknown, task: TaskDetailViewModel): void {
    if (!task.steps || task.steps.length === 0) {
      this.handleNotFound();
      return;
    }

    // If a valid step ID was not provided, redirect to the first step
    if (stepId === null) {
      // To save on reloading the task, pass it through to ourselves and replace the current /tasks/XYZ[/steps] URL
      const navigationExtras: NavigationExtras = {
        replaceUrl: true,
        state: {[TaskComponent.EXISTING_TASK_KEY]: task}
      };

      this.navigateToStep(taskId, task.steps[0].id, navigationExtras);
      return;
    }

    this.task.set(task);
    this.maxEditableStepIndex.set(this.findMaxEditableStepIndex(task.steps));

    const stepIdNumber = Number(stepId);
    const stepIndex = this.findTaskStepIndex(stepIdNumber);

    // If the step ID was not found OR is after the max editable step, current business rules are to redirect to the max editable step
    if (stepIndex === -1 || stepIndex > this.maxEditableStepIndex()) {
      // Replace the invalid URL with the new one
      const navigationExtras: NavigationExtras = {
        replaceUrl: true,
      };
      this.navigateToStep(taskId, task.steps[this.maxEditableStepIndex()].id, navigationExtras);
      return;
    }

    this.currentStep.set(this.task().steps[stepIndex]);
    this.currentStepIndex.set(stepIndex);

    this.loadTaskStepDetailsAndRender(taskId, this.currentStep().id);
  }

  private loadTaskStepDetailsAndRender(taskId: string, stepId: number): void {
    this.dataLoading.set(true);
    this.stepLoadError.set(false);

    const subscription = this.taskService.getTaskStep(taskId, stepId).subscribe({
      next: taskStepDetailDto => {
        const taskStep = this.taskConverterService.convertTaskStepDetailDtoToTaskStepDetailViewModel(taskStepDetailDto);
        this.replaceCurrentStep(taskStep);
        this.dataLoading.set(false);
        this.renderStepComponent();
      },
      error: () => {
        this.dataLoading.set(false);
        this.stepLoadError.set(true);
      }
    });

    this.subscriptions.add(subscription);
  }

  retryLoadStep(): void {
    if (this.task() && this.currentStep()) {
      this.loadTaskStepDetailsAndRender(this.task().id, this.currentStep().id);
    }
  }

  private replaceCurrentStep(taskStep: TaskStepDetailViewModel): void {
    const updatedSteps = [...this.task().steps];
    updatedSteps[this.currentStepIndex()] = taskStep;

    this.task.set({
      ...this.task(),
      steps: updatedSteps
    });

    this.currentStep.set(taskStep);
  }

  private navigateToStep(taskId: string, stepId: number, navigationExtras?: NavigationExtras) {
    const targetRouterLinkCommands = APP_TASK_STEP.toRouterLinkCommands(taskId, String(stepId));
    this.router.navigate(targetRouterLinkCommands, navigationExtras);
  }

  private findMaxEditableStepIndex(steps: TaskStepDetailViewModel[]): number {
    const result = steps.findIndex(taskStep => !taskStep.completed);

    // If there are no unsubmitted steps, then all the steps were already submitted and the max editable step is the last one
    if (result === -1) {
      return steps.length - 1;
    }

    return result;
  }

  private findTaskStepIndex(stepId: number): number {
    return this.task().steps.findIndex(taskStep => taskStep.id === stepId);
  }

  private renderStepComponent(): void {
    this.dataLoading.set(false);

    // Our placeholder directive is inside an *ngIf so we need to tell Angular to run change detection to add it to the DOM
    this.changeDetectorRef.detectChanges();

    this.subscriptions.unsubscribe();
    const container = this.taskDirective().viewContainerRef;
    container.clear();

    const taskComponentType = this.taskComponentMappingService.map(this.currentStep().type);
    const taskComponentRef = container.createComponent(taskComponentType);
    const taskComponent = taskComponentRef.instance;

    const state = new TaskStepStateViewModel(this.currentStepIndex() + 1, this.task().steps.length, this.task().readonly);
    taskComponent.initialize(this.task(), this.currentStep(), state);

    const backSubscription = taskComponent.previousClicked.subscribe(() => this.goToPreviousStep());
    const nextSubscription = taskComponent.nextClicked.subscribe(() => this.goToNextStep());
    const closeSubscription = taskComponent.closeClicked.subscribe(() => this.closeTask());
    const updateSubscription = taskComponent.taskUpdated.subscribe(updatedTask => this.updateTask(updatedTask));
    const processingSubscription = taskComponent.processingUpdated.subscribe(processing => this.stepProcessing.set(processing));

    this.subscriptions = new Subscription();
    this.subscriptions.add(backSubscription);
    this.subscriptions.add(nextSubscription);
    this.subscriptions.add(closeSubscription);
    this.subscriptions.add(updateSubscription);
    this.subscriptions.add(processingSubscription);
  }

  private goToPreviousStep(): void {
    if (this.hasPrevious()) {
      this.goToStepIndex(this.currentStepIndex() - 1);
    }
  }

  private goToNextStep(): void {
    if (this.hasNext()) {
      this.goToStepIndex(this.currentStepIndex() + 1);
    } else {
      this.closeTask();
    }
  }

  private hasPrevious() {
    return this.currentStepIndex() > 0;
  }

  private hasNext() {
    return this.currentStepIndex() < (this.task().steps.length - 1);
  }

  public closeTask(): void {
    this.router.navigate(APP_TASKS.toRouterLinkCommands());
  }

  public goToStepIndex(stepIndex: number): void {
    if (stepIndex !== this.currentStepIndex() && stepIndex <= this.maxEditableStepIndex()) {
      const stepId = this.task().steps[stepIndex].id;
      this.navigateToStep(this.task().id, stepId);
    }
  }

  private updateTask(updateDto: TaskStepUpdateDto) {
    const updatedStep = this.taskConverterService.convertTaskStepDetailDtoToTaskStepDetailViewModel(updateDto.step);
    const existingSteps = [...this.task().steps];
    const matchingStepIndex = existingSteps.findIndex(step => step.id === updatedStep.id);
    const targetStepIndex = matchingStepIndex >= 0 ? matchingStepIndex : this.currentStepIndex();

    existingSteps[targetStepIndex] = updatedStep;

    const updatedTask = this.taskConverterService.convertTaskDtoAndStepsToTaskDetailViewModel(updateDto.task, existingSteps);

    this.task.set(updatedTask);
    this.currentStepIndex.set(targetStepIndex);
    this.currentStep.set(updatedStep);
    this.maxEditableStepIndex.set(this.findMaxEditableStepIndex(updatedTask.steps));
  }
}
